import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  getDoc,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
  serverTimestamp,
  arrayUnion,
  arrayRemove,
  increment
} from 'firebase/firestore'
import { db } from '../../lib/firebase'
import {
  Comment,
  CommentVisibility,
  CreateCommentData,
  UpdateCommentData,
  CommentFilters,
  CommentCategory
} from '../../types/comments'
import { getCoordinatesAtDistance } from '../../utils/routeDistanceHelpers'
import { RouteData } from '@cya-routes/shared'
import { CommentSchema, validateWithDetails, sanitizeString } from './validation/schemas'

const COMMENTS_COLLECTION = 'comments'
const COMMENT_VISIBILITY_COLLECTION = 'comment_visibility'

/**
 * Create a new comment
 */
export async function createComment(
  commentData: CreateCommentData,
  authorId: string,
  authorName: string,
  authorPhoto?: string,
  route?: RouteData
): Promise<string> {
  // Sanitize input strings
  const sanitizedData = {
    ...commentData,
    text: sanitizeString(commentData.text, 1000),
    routeId: sanitizeString(commentData.routeId, 100)
  }

  // Calculate distance along route for pinned comments if coordinates provided
  let distanceAlongRoute = commentData.distanceAlongRoute
  if (commentData.type === 'pinned' && commentData.coordinates && route && !distanceAlongRoute) {
    // Find the closest point on the route to calculate distance
    // This is a simplified implementation - in production you'd want more sophisticated distance calculation
    const routeCoords = route.coordinates || []
    if (routeCoords.length > 0) {
      // Find closest coordinate point (simplified)
      let minDistance = Infinity
      let closestIndex = 0
      
      routeCoords.forEach((coord, index) => {
        const distance = Math.sqrt(
          Math.pow(coord[0] - commentData.coordinates!.lng, 2) +
          Math.pow(coord[1] - commentData.coordinates!.lat, 2)
        )
        if (distance < minDistance) {
          minDistance = distance
          closestIndex = index
        }
      })
      
      // Calculate cumulative distance to this point
      distanceAlongRoute = 0
      for (let i = 0; i < closestIndex; i++) {
        const [lng1, lat1] = routeCoords[i]
        const [lng2, lat2] = routeCoords[i + 1]
        
        // Simple distance approximation
        const dLat = lat2 - lat1
        const dLng = lng2 - lng1
        const avgLat = (lat1 + lat2) / 2 * Math.PI / 180
        
        const x = dLng * Math.cos(avgLat) * 111.32
        const y = dLat * 111.32
        const segmentDist = Math.sqrt(x * x + y * y)
        
        distanceAlongRoute += segmentDist
      }
    }
  }

  // Build comment object, excluding undefined fields
  const comment: any = {
    routeId: sanitizedData.routeId,
    type: commentData.type,
    category: commentData.category,
    text: sanitizedData.text,
    ownerId: sanitizeString(authorId, 100),
    ownerDisplayName: sanitizeString(authorName, 100),
    ownerPhoto: authorPhoto ? sanitizeString(authorPhoto, 500) : null,
    authorId,
    authorName,
    authorReputation: 0,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    resolved: false,
    confirmations: [],
    disputes: [],
    helpful: [],
    replyCount: 0
  }

  // Add optional fields only if they're defined
  if (commentData.coordinates) {
    comment.coordinates = commentData.coordinates
  }
  if (distanceAlongRoute !== undefined) {
    comment.distanceAlongRoute = distanceAlongRoute
  }
  if (commentData.photos && commentData.photos.length > 0) {
    comment.photos = commentData.photos
  }
  if (authorPhoto) {
    comment.authorPhoto = authorPhoto
  }
  if (commentData.validUntil) {
    comment.validUntil = Timestamp.fromDate(commentData.validUntil)
  }
  if (commentData.parentId) {
    comment.parentId = commentData.parentId
  } else {
    // Explicitly set parentId to null for top-level comments
    comment.parentId = null
  }

  // Validate the comment data before saving
  const validatedComment = validateWithDetails(CommentSchema, comment, 'comment')
  
  const docRef = await addDoc(collection(db, COMMENTS_COLLECTION), validatedComment)
  
  // If this is a reply, increment parent's reply count
  if (commentData.parentId) {
    const parentRef = doc(db, COMMENTS_COLLECTION, commentData.parentId)
    await updateDoc(parentRef, {
      replyCount: increment(1)
    })
  }

  return docRef.id
}

/**
 * Get comments for a route
 */
export async function getRouteComments(
  routeId: string,
  filters?: CommentFilters,
  limitCount = 100
): Promise<Comment[]> {
  let q = query(
    collection(db, COMMENTS_COLLECTION),
    where('routeId', '==', routeId),
    orderBy('createdAt', 'desc'),
    limit(limitCount)
  )

  // Apply category filter
  if (filters?.categories && filters.categories.length > 0) {
    q = query(q, where('category', 'in', filters.categories))
  }

  // Apply recency filter
  if (filters?.recency && filters.recency !== 'all') {
    const now = new Date()
    let cutoffDate: Date
    
    switch (filters.recency) {
      case '24h':
        cutoffDate = new Date(now.getTime() - 24 * 60 * 60 * 1000)
        break
      case '7d':
        cutoffDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
        break
      case '30d':
        cutoffDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
        break
      default:
        cutoffDate = new Date(0)
    }
    
    q = query(q, where('createdAt', '>=', Timestamp.fromDate(cutoffDate)))
  }

  const snapshot = await getDocs(q)
  const comments = snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  } as Comment))
  
  // Debug log to see comment structure
  if (comments.length > 0) {
    console.log('🔍 Sample comment structure:', JSON.stringify(comments[0], null, 2))
    console.log('🔍 Comment types:', comments.map(c => ({ id: c.id, type: c.type, hasCoords: !!c.coordinates })))
  }

  // Apply additional filters that can't be done in Firestore query
  let filteredComments = comments

  // Filter by author
  if (filters?.author === 'me') {
    // This would need to be implemented with current user context
    // filteredComments = filteredComments.filter(comment => comment.authorId === currentUserId)
  }

  // Hide resolved if requested
  if (filters?.hideResolved) {
    filteredComments = filteredComments.filter(comment => !comment.resolved)
  }

  return filteredComments
}

/**
 * Get a specific comment by ID
 */
export async function getComment(commentId: string): Promise<Comment | null> {
  const docRef = doc(db, COMMENTS_COLLECTION, commentId)
  const snapshot = await getDoc(docRef)
  
  if (!snapshot.exists()) {
    return null
  }

  return {
    id: snapshot.id,
    ...snapshot.data()
  } as Comment
}

/**
 * Update a comment
 */
export async function updateComment(
  commentId: string,
  updates: UpdateCommentData
): Promise<void> {
  const docRef = doc(db, COMMENTS_COLLECTION, commentId)
  
  // Sanitize text if provided
  const sanitizedUpdates = {
    ...updates,
    text: updates.text ? sanitizeString(updates.text, 1000) : updates.text
  }
  
  const updateData: any = {
    ...sanitizedUpdates,
    updatedAt: serverTimestamp()
  }

  if (updates.validUntil) {
    updateData.validUntil = Timestamp.fromDate(updates.validUntil)
  }

  if (updates.resolved !== undefined) {
    updateData.resolved = updates.resolved
    if (updates.resolved) {
      updateData.resolvedAt = serverTimestamp()
    }
  }

  // Validate only the fields being updated
  const partialSchema = CommentSchema.partial()
  const validatedUpdate = validateWithDetails(partialSchema, updateData, 'comment update')

  await updateDoc(docRef, validatedUpdate)
}

/**
 * Delete a comment
 */
export async function deleteComment(commentId: string): Promise<void> {
  const comment = await getComment(commentId)
  if (!comment) return

  // If this is a reply, decrement parent's reply count
  if (comment.parentId) {
    const parentRef = doc(db, COMMENTS_COLLECTION, comment.parentId)
    await updateDoc(parentRef, {
      replyCount: increment(-1)
    })
  }

  // Delete all replies to this comment
  if (comment.replyCount && comment.replyCount > 0) {
    const repliesQuery = query(
      collection(db, COMMENTS_COLLECTION),
      where('parentId', '==', commentId)
    )
    const repliesSnapshot = await getDocs(repliesQuery)
    
    const deletePromises = repliesSnapshot.docs.map(doc => deleteDoc(doc.ref))
    await Promise.all(deletePromises)
  }

  // Delete the comment itself
  await deleteDoc(doc(db, COMMENTS_COLLECTION, commentId))
}

/**
 * Confirm a comment (mark as accurate)
 */
export async function confirmComment(commentId: string, userId: string): Promise<void> {
  const docRef = doc(db, COMMENTS_COLLECTION, commentId)
  await updateDoc(docRef, {
    confirmations: arrayUnion(userId),
    disputes: arrayRemove(userId) // Remove from disputes if previously disputed
  })
}

/**
 * Dispute a comment (mark as inaccurate)
 */
export async function disputeComment(commentId: string, userId: string): Promise<void> {
  const docRef = doc(db, COMMENTS_COLLECTION, commentId)
  await updateDoc(docRef, {
    disputes: arrayUnion(userId),
    confirmations: arrayRemove(userId) // Remove from confirmations if previously confirmed
  })
}

/**
 * Mark comment as helpful
 */
export async function markCommentHelpful(commentId: string, userId: string): Promise<void> {
  const docRef = doc(db, COMMENTS_COLLECTION, commentId)
  await updateDoc(docRef, {
    helpful: arrayUnion(userId)
  })
}

/**
 * Remove helpful mark from comment
 */
export async function unmarkCommentHelpful(commentId: string, userId: string): Promise<void> {
  const docRef = doc(db, COMMENTS_COLLECTION, commentId)
  await updateDoc(docRef, {
    helpful: arrayRemove(userId)
  })
}

/**
 * Get comment visibility settings for user and route
 */
export async function getCommentVisibility(
  userId: string,
  routeId: string
): Promise<CommentVisibility | null> {
  const docRef = doc(db, COMMENT_VISIBILITY_COLLECTION, `${userId}_${routeId}`)
  const snapshot = await getDoc(docRef)
  
  if (!snapshot.exists()) {
    return null
  }

  return snapshot.data() as CommentVisibility
}

/**
 * Update comment visibility settings
 */
export async function updateCommentVisibility(
  visibility: Partial<CommentVisibility> & { userId: string; routeId: string }
): Promise<void> {
  const docRef = doc(db, COMMENT_VISIBILITY_COLLECTION, `${visibility.userId}_${visibility.routeId}`)
  
  const updateData = {
    ...visibility,
    lastViewedAt: serverTimestamp()
  }

  await updateDoc(docRef, updateData)
}

/**
 * Get pinned comments for a route sorted by distance
 */
export async function getPinnedComments(routeId: string): Promise<Comment[]> {
  try {
    const q = query(
      collection(db, COMMENTS_COLLECTION),
      where('routeId', '==', routeId),
      where('type', '==', 'pinned'),
      orderBy('distanceAlongRoute', 'asc')
    )

    const snapshot = await getDocs(q)
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as Comment))
  } catch (error) {
    console.log('⚠️ getPinnedComments query failed, trying without orderBy:', error)
    
    // Try simpler query without orderBy (might not have index)
    const q = query(
      collection(db, COMMENTS_COLLECTION),
      where('routeId', '==', routeId),
      where('type', '==', 'pinned')
    )
    
    const snapshot = await getDocs(q)
    const comments = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as Comment))
    
    // Sort manually
    return comments.sort((a, b) => (a.distanceAlongRoute || 0) - (b.distanceAlongRoute || 0))
  }
}

/**
 * Get general discussion comments for a route
 */
export async function getGeneralComments(routeId: string): Promise<Comment[]> {
  try {
    const q = query(
      collection(db, COMMENTS_COLLECTION),
      where('routeId', '==', routeId),
      where('type', '==', 'general'),
      where('parentId', '==', null), // Only top-level comments
      orderBy('createdAt', 'desc')
    )

    const snapshot = await getDocs(q)
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as Comment))
  } catch (error) {
    console.log('⚠️ getGeneralComments query failed, trying without compound query:', error)
    
    // Try simpler query
    const q = query(
      collection(db, COMMENTS_COLLECTION),
      where('routeId', '==', routeId),
      where('type', '==', 'general')
    )
    
    const snapshot = await getDocs(q)
    const comments = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as Comment))
    
    // Filter and sort manually
    return comments
      .filter(c => !c.parentId)
      .sort((a, b) => (b.createdAt?.toMillis() || 0) - (a.createdAt?.toMillis() || 0))
  }
}

/**
 * Get replies to a comment
 */
export async function getCommentReplies(parentId: string): Promise<Comment[]> {
  const q = query(
    collection(db, COMMENTS_COLLECTION),
    where('parentId', '==', parentId),
    orderBy('createdAt', 'asc')
  )

  const snapshot = await getDocs(q)
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  } as Comment))
}